import { useState, useCallback } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import './App.css';
import ErrorScreen from './components/ErrorScreen';
import PinScreen from './components/PinScreen';
import ShowcasePlayer from './components/ShowcasePlayer';

/* ═══════════════════════════════════════════════════════════════════════════
   App — TV Showcase entry point

   Route structure:
     /           → HomePage (PIN pairing OR auto-redirect to saved showcase)
     /s/:token   → ShowcasePlayer (direct URL access — always works)
     *           → 404

   The HomePage checks localStorage for a previously paired token.
   If found, auto-navigates to /s/{token}. If not, shows the PIN screen.
   ═══════════════════════════════════════════════════════════════════════════ */

/** localStorage key for the paired showcase token. */
const PAIRED_TOKEN_KEY = 'showcase-paired-token';

function HomePage() {
  const navigate = useNavigate();
  const [storedToken] = useState(() => {
    try {
      return localStorage.getItem(PAIRED_TOKEN_KEY);
    } catch {
      return null;
    }
  });

  const handlePaired = useCallback(
    (token: string) => {
      navigate(`/s/${token}`, { replace: true });
    },
    [navigate],
  );

  const handleUnpair = useCallback(() => {
    try {
      localStorage.removeItem(PAIRED_TOKEN_KEY);
    } catch { /* ignore */ }
    // Force re-render to show PIN screen
    window.location.reload();
  }, []);

  // If we have a stored token, redirect directly to the showcase
  if (storedToken) {
    return <Navigate to={`/s/${storedToken}`} replace />;
  }

  // No stored token — show PIN pairing screen
  return <PinScreen onPaired={handlePaired} />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/s/:token" element={<ShowcasePlayer />} />
        <Route
          path="*"
          element={
            <ErrorScreen
              title="Not Found"
              message="The page you're looking for doesn't exist."
            />
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
